/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import Squircle from '@ziynx/components/squircles';
import { LoadingSpinner } from '@ziynx/components/utilities';
import Colors from '@ziynx/theme/colors';
import Image from './Image';

type Props = $ReadOnly<{|
  /**
   * If image is loading
   */
  isLoading?: boolean,
  /**
   * Text to fallback to if no imageSource, will be parsed to initials
   */
  placeholderText?: string,
  /**
  * Source, uri for image as children, if any
  */
  imageSource?: string,
  /**
   * Alt text for image
   */
  imageAltText?: string,
  /**
  * Size of the avatar
  */
  size: 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge' | 'extraExtraLarge',
  /**
  * Overlay with text, if any
  */
  overlayText?: string,
  /**
  * On click event for avatar
  */
  onClick?: () => void,
|}>

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'inherit')};
`;

const ImageAvatar = (props: Props): React$Node => {
  const {
    imageSource, isLoading, placeholderText, imageAltText, size, overlayText, onClick,
  } = props;

  const handleError = (event) => {
    // eslint-disable-next-line no-param-reassign
    event.target.src = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';
  };

  const _getChildren = (): any => {
    if (isLoading) return <LoadingSpinner size="veryTiny" />;

    if (!imageSource) return undefined;

    return (
      <Image
        isBackground
        onError={handleError}
        src={imageSource}
        alt={imageAltText}
      />
    );
  };

  return (
    <AvatarWrapper
      onClick={onClick}
    >
      <Squircle
        text={placeholderText}
        size={size}
        overlayText={overlayText}
        inverted={!!placeholderText}
        invertedColor={Colors?.White}
        invertedBackgroundColor={Colors?.GrayLight}
      >
        {_getChildren()}
      </Squircle>
    </AvatarWrapper>
  );
};

ImageAvatar.defaultProps = {
  imageSource: undefined,
  overlayText: undefined,
};

export default ImageAvatar;
